import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { type Comment } from '@tourfold/libraries/tf-api-client-public-v2';
import {
  AttachmentUploadError,
  type AttachmentUploadErrorReason,
} from '../services/comment-attachments.service';
import { CommentsService } from '../services/comments.service';
import { RichEditorComponent } from './rich-editor.component';

/**
 * Inline markdown composer for posting a new comment or reply.
 *
 * Used in two places:
 *  - The main composer at the top of `<tf-comments>` for new root comments.
 *  - The per-row reply composer that toggles on under each row when the user clicks "Reply".
 *
 * The composer is in-memory only — the body resets on every mount. We previously autosaved to
 * localStorage but removed it as a privacy measure (keys not scoped by user, persisted past
 * logout, XSS-readable, and exposed `attachment:<uuid>` ids for in-progress uploads).
 *
 * The component owns the textarea state but does NOT own the comment list. On a successful
 * create it emits `created` with the server's `Comment`; the parent decides how to merge it
 * (optimistic / refresh / etc).
 */
@Component({
  selector: 'tf-comment-composer',
  standalone: true,
  imports: [FormsModule, TranslateModule, ButtonModule, RichEditorComponent],
  templateUrl: './comment-composer.component.html',
  styleUrl: './comment-composer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentComposerComponent {
  private readonly commentsService = inject(CommentsService);
  private readonly messageService = inject(MessageService, { optional: true });
  private readonly translate = inject(TranslateService);

  /** Mirrors the server constraint on `body_markdown` (see `CreateCommentRequest` Kotlin DTO). */
  static readonly MAX_BODY_LENGTH = 10_000;

  readonly targetType = input.required<string>();
  readonly targetId = input.required<string>();
  /** Set when this composer is posting a reply; omit for the main (root) composer. */
  readonly parentCommentId = input<string | undefined>(undefined);
  /**
   * Reply composers default to autofocus + a "Cancel" button visible. The main composer keeps
   * focus opt-in and never shows Cancel — see `<tf-comments>`.
   */
  readonly autofocus = input<boolean>(false);
  readonly showCancel = input<boolean>(false);

  readonly created = output<Comment>();
  readonly cancelled = output<void>();
  /**
   * Fires on every body change (and on autoload of a saved draft) so the host can surface the
   * current draft text to other panels — primarily the AI assistant page-context override
   * which shows the assistant what the user is currently writing. Emitted without debounce
   * because the consumers are signal-driven and pay nothing per keystroke.
   */
  readonly draftChange = output<string>();

  protected readonly body = signal('');
  protected readonly submitting = signal(false);
  /**
   * Latest `attachment_ids` snapshot from `<tf-rich-editor>`. The editor emits whenever the union
   * of (body tokens) ∪ (non-inline pending pills) changes; we just mirror the value and forward
   * it on submit. Keeping the state here (instead of re-deriving on submit) means a stale
   * non-inline pending pill that got removed mid-upload doesn't leak its id into the payload.
   */
  protected readonly attachmentIds = signal<readonly string[]>([]);
  /**
   * Latest attachment upload error surfaced by `<tf-rich-editor>`, keyed by the failed pill's
   * `localId` so banner + pill dismissals stay linked: dismissing the banner removes the pill;
   * removing the pill (via its X) clears the banner. Cleared on submit / cancel.
   */
  protected readonly uploadErrorState = signal<
    { reason: AttachmentUploadErrorReason; localId: string } | null
  >(null);

  /** Handle to the inner editor so the banner-dismiss handler can call `removePending`. */
  private readonly editor = viewChild.required(RichEditorComponent);

  /**
   * Translation key for the active upload-error banner. Computed so the template stays simple —
   * `mime-not-allowed` → `COMMENTS.ATTACHMENT_ERROR.MIME_NOT_ALLOWED`.
   */
  protected readonly uploadErrorMessageKey = computed<string | null>(() => {
    const reason = this.uploadErrorState()?.reason;
    if (!reason) return null;
    return `COMMENTS.ATTACHMENT_ERROR.${reason.toUpperCase().replaceAll('-', '_')}`;
  });

  protected readonly remaining = computed(
    () => CommentComposerComponent.MAX_BODY_LENGTH - this.body().length,
  );
  protected readonly isOverLimit = computed(() => this.body().length > CommentComposerComponent.MAX_BODY_LENGTH);
  protected readonly isEmpty = computed(() => this.body().trim().length === 0);
  protected readonly canSubmit = computed(
    () => !this.isEmpty() && !this.isOverLimit() && !this.submitting(),
  );

  constructor() {
    // Body resets on every mount (no persistence). Re-emit on every change so the AI assistant
    // page-context override can mirror what the user is currently writing — that's the only
    // consumer that needs to see the in-flight body outside the component.
    effect(() => this.draftChange.emit(this.body()));

    // Auto-focus is handled by `<tf-rich-editor>` itself (its `[autofocus]` input maps to
    // Tiptap's `autofocus: 'end'` mode), so the composer no longer needs to chase the
    // textarea element manually.
  }

  protected onCancel(): void {
    this.body.set('');
    this.uploadErrorState.set(null);
    this.cancelled.emit();
  }

  /**
   * Captures a per-upload failure surfaced by `<tf-rich-editor>` so the template can banner it.
   * The `localId` correlates with the failed pill in the editor's row — used by
   * {@link dismissUploadError} to also remove the pill, and by {@link onPendingRemoved} to clear
   * the banner when the user removes the pill directly.
   */
  protected onUploadError(event: { error: AttachmentUploadError; localId: string }): void {
    this.uploadErrorState.set({ reason: event.error.reason, localId: event.localId });
  }

  /**
   * User clicked X on the banner. Clears the banner AND removes the matching failed pill so the
   * two error surfaces stay in lockstep — without this the user would have to dismiss both.
   */
  protected dismissUploadError(): void {
    const state = this.uploadErrorState();
    this.uploadErrorState.set(null);
    if (state) this.editor().removePending(state.localId);
  }

  /**
   * Editor told us a pill was removed. If it's the same id the banner is keyed off, the user
   * already saw the error and dealt with it — clear the banner so we don't leave an orphan
   * notification floating above the editor.
   */
  protected onPendingRemoved(localId: string): void {
    if (this.uploadErrorState()?.localId === localId) {
      this.uploadErrorState.set(null);
    }
  }

  protected async onSubmit(): Promise<void> {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    try {
      const body = this.body().trim();
      const created = await this.commentsService.create({
        targetType: this.targetType(),
        targetId: this.targetId(),
        bodyMarkdown: body,
        parentCommentId: this.parentCommentId(),
        // Sourced from the editor's `attachmentIdsChange` snapshot — the editor unions inline
        // body tokens with non-inline pending pill ids, so Backspaced-out images and removed
        // pills both fall out naturally.
        attachmentIds: this.attachmentIds(),
      });
      this.body.set('');
      this.uploadErrorState.set(null);
      this.created.emit(created);
    } catch {
      // Mirrors the rest of the codebase (e.g. instance-list.component.ts, settings-general.ts) —
      // PrimeNG's MessageService doesn't pipe through the translate pipe, so resolve the keys here.
      this.messageService?.add({
        severity: 'error',
        summary: this.translate.instant('COMMENTS.COMPOSER.SUBMIT_ERROR_TITLE'),
        detail: this.translate.instant('COMMENTS.COMPOSER.SUBMIT_ERROR_DETAIL'),
        life: 5000,
      });
    } finally {
      this.submitting.set(false);
    }
  }

}
